import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteAlertForCurrentUser,
  fetchAlerts,
  fetchUnreadAlertCount,
  markAlertAsRead,
  markAlertAsUnread,
} from "./alertsApi";
import { maxSeverityFromAlerts } from "./alertSeverityUtils";

const DEFAULT_FILTER = "all";
const POLL_INTERVAL_MS = 60000;

function toMessage(error) {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error?.message) return String(error.message);
  return "Unexpected error";
}

export function useAlertsCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [maxUnreadSeverity, setMaxUnreadSeverity] = useState(null);

  const loadAlerts = useCallback(async (nextFilter = DEFAULT_FILTER, options = {}) => {
    const { silent = false } = options;

    try {
      if (!silent) setLoading(true);
      setError("");
      const data = await fetchAlerts({ status: nextFilter });
      setAlerts(data.alerts);

      const serverUnread = Number(data?.meta?.unread_count);
      const unreadAlerts = data.alerts.filter((a) => !a?.coach_state?.is_read);

      if (Number.isFinite(serverUnread)) {
        setUnreadCount(serverUnread);
      } else {
        setUnreadCount(unreadAlerts.length);
      }

      if (nextFilter === "unread" || nextFilter === "all") {
        const sourceAlerts = nextFilter === "unread" ? data.alerts : unreadAlerts;
        setMaxUnreadSeverity(maxSeverityFromAlerts(sourceAlerts));
      }
    } catch (e) {
      setAlerts([]);
      setError(toMessage(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const { count, maxSeverity } = await fetchUnreadAlertCount();
      setUnreadCount(count);
      setMaxUnreadSeverity(count > 0 ? maxSeverity : null);
    } catch {
      // Silent polling failure: keep previous value.
    }
  }, []);

  const openModal = useCallback(() => {
    setIsOpen(true);
    loadAlerts(filter);
  }, [filter, loadAlerts]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const changeFilter = useCallback(
    (nextFilter) => {
      setFilter(nextFilter);
      loadAlerts(nextFilter);
    },
    [loadAlerts]
  );

  const reloadCurrent = useCallback(() => {
    return loadAlerts(filter);
  }, [filter, loadAlerts]);

  const markRead = useCallback(
    async (alertId) => {
      try {
        setError("");
        await markAlertAsRead(alertId);
        await loadAlerts(filter, { silent: true });
      } catch (e) {
        setError(toMessage(e));
      }
    },
    [filter, loadAlerts]
  );

  const markUnread = useCallback(
    async (alertId) => {
      try {
        setError("");
        await markAlertAsUnread(alertId);
        await loadAlerts(filter, { silent: true });
      } catch (e) {
        setError(toMessage(e));
      }
    },
    [filter, loadAlerts]
  );

  const removeAlert = useCallback(
    async (alertId) => {
      try {
        setError("");
        await deleteAlertForCurrentUser(alertId);
        await loadAlerts(filter, { silent: true });
      } catch (e) {
        setError(toMessage(e));
      }
    },
    [filter, loadAlerts]
  );

  useEffect(() => {
    refreshUnreadCount();
    const id = window.setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refreshUnreadCount]);

  const hasUnread = useMemo(() => unreadCount > 0, [unreadCount]);

  return {
    isOpen,
    openModal,
    closeModal,
    alerts,
    filter,
    changeFilter,
    loading,
    error,
    unreadCount,
    maxUnreadSeverity,
    hasUnread,
    refreshUnreadCount,
    reloadCurrent,
    markRead,
    markUnread,
    removeAlert,
  };
}
